import {
  Kpi,
  KpiResult,
  KpiSummary,
  KpiType,
  KpiValue
} from '@/api-client/models/buildingScore/Contracts'
import { ColorScaleConfig } from '@/utils/colorScale'
import { formatNumber } from '@/utils/math'
import { Ref } from 'vue'

export function getTitle(kpi: Kpi, locale: Ref<string>): string {
  let title = { sv: '', en: '' }
  switch (kpi.kpi) {
    case 'BUILDING_SCORE':
      title = { sv: 'Score', en: 'Score' }
      break
    case 'TREND':
      switch (kpi.parameters.type) {
        case 'ATTRACTIVENESS':
          title = { sv: 'Trend Attraktivitet', en: 'Trend Attractiveness' }
          break
        case 'CONDITION':
          title = { sv: 'Trend Skick', en: 'Trend Condition' }
          break
      }
      break
  }
  return title[locale.value] ?? ''
}

export function getKpiInfo(kpi: Kpi, locale: Ref<string>): string {
  let info = { sv: '', en: '' }
  switch (kpi.kpi) {
    case 'BUILDING_SCORE':
      info = {
        sv: 'En byggnad analyseras utifrån dess skick och attraktivitet.\n\n**Skick**\n\nSkick bedöms utifrån hur moderniserad, funktionsduglig eller hur stora brister en byggnad har. Skick bedöms utifrån en skala från 1-3 där 1 är lågt skick, 2 är medelskick och 3 är högt skick.\n\n*Högt skick:*\n\nNyligen moderniserat, renoverat eller nyproduktion.\n\n*Medelskick:*\n\nFunktionsdugliga lokaler i fas med underhåll med viss variation. 1990-2000-talsstandard.\n\n*Lågt skick:*\n\nStora brister, i större grad uttjänta tekniska installationer. 1970-1980-talsstandard eller äldre. Åtgärder ryms inte inom normal förvaltning.\n\n**Attraktivitet**\n\nAttraktivitet bedöms utifrån marknadens förväntningar, grad av specialanpassning och hur svår byggnaden är att ställa om till annan verksamhet eller funktion. Attraktivitet bedöms utifrån en skala från 1-3 där 1 är låg attraktivitet, 2 är medelattraktivitet och 3 är hög attraktivitet.\n\n*Hög attraktivitet*\n\nÖverträffar marknadens förväntningar i detta läge. Mindre grad av specialanpassning.\n\nRelativt enkelt att ställa om till annan verksamhet.\n\n*Medelattraktivitet*\n\nMotsvarar efterfrågan på marknaden i detta läge. Möjlighet till viss anpassning och ändrad användning.\n\nVissa investeringar krävs för omställning till annan verksamhet/funktion.\n\n*Låg attraktivitet*\n\nMöter inte alls marknadens förväntningar i detta läge. Hög andel mindre kvalitativa ytor.\n\nSvår att ställa om. Kräver större satsningar.',
        en: 'A building is analyzed based on its condition and attractiveness.\n\n**Condition**\n\nCondition is assessed based on how modernized, functional or how big defects a building has. Condition is assessed on a scale from 1-3 where 1 is low condition, 2 is medium condition and 3 is high condition.\n\n*High condition:*\n\nRecently modernized, renovated or new construction.\n\n*Medium condition:*\n\nFunctional premises in phase with maintenance with some variation. 1990-2000 standard.\n\n*Low condition:*\n\nMajor defects, more worn-out technical installations. 1970-1980 standard or older. Measures do not fit within normal management.\n\n**Attractiveness**\n\nAttractiveness is assessed based on market expectations, degree of customization and how difficult the building is to convert to another activity or function. Attractiveness is assessed on a scale from 1-3 where 1 is low attractiveness, 2 is medium attractiveness and 3 is high attractiveness.\n\n*High attractiveness*\n\nExceeds market expectations in this location. Less degree of customization.\n\nRelatively easy to convert to another activity.\n\n*Medium attractiveness*\n\nMatches the demand on the market in this location. Possibility for some adaptation and changed use.\n\nSome investments are required for conversion to another activity/function.\n\n*Low attractiveness*\n\nDoes not meet market expectations at all in this location. High proportion of less qualitative areas.\n\nDifficult to convert. Requires larger investments.'
      }
      break
    case 'TREND':
      switch (kpi.parameters.type) {
        case 'ATTRACTIVENESS':
          info = {
            sv: 'Trend avseende attraktivitet visar hur måttet attraktivitet har förändrats i jämförelse med föregående år.',
            en: 'The trend regarding attractiveness shows how the measure of attractiveness has changed compared to the previous year.'
          }
          break
        case 'CONDITION':
          info = {
            sv: 'Trend avseende skick visar hur måttet skick har förändrats i jämförelse med föregående år',
            en: 'The trend concerning condition shows how the measure of condition has changed in comparison to the previous year.'
          }
          break
      }
      break
  }

  return info[locale.value] ?? ''
}

export function getLabel(type: KpiType, value: number | null): string {
  switch (type) {
    case 'BUILDING_SCORE':
      return buildingScoreConfig(value).label
    case 'TREND':
      return trendConfig(value).label
  }
}

export function getColor(data: KpiResult | undefined): string {
  if (data === undefined) return '#D9D9D9'
  switch (data.kpi) {
    case 'BUILDING_SCORE':
      return buildingScoreConfig(data.summary.value).color
    case 'TREND':
      return trendConfig(data.summary.value).color
  }
}

export function getIcon(data: KpiResult | undefined): string | undefined {
  if (data === undefined) return undefined
  switch (data.kpi) {
    case 'BUILDING_SCORE':
      return undefined
    case 'TREND':
      return trendConfig(data.summary.value).imgSrc
  }
}

function buildingScoreConfig(value: number | null): { label: string; color: string } {
  if (value === null) return { label: '-', color: '#D9D9D9' }
  const rounded = Math.round(value)
  switch (rounded) {
    case 2:
    case 3:
      return { label: 'general__text__red', color: '#FF4D4F' }
    case 4:
      return { label: 'general__text__blue', color: '#40A9FF' }
    case 5:
    case 6:
      return { label: 'general__text__green', color: '#73D13D' }
    default:
      return { label: 'general__text__unknown', color: '#D9D9D9' }
  }
}

function trendConfig(value: number | null): {
  label: string
  color: string
  imgSrc: string
} {
  if (value === null) return { label: '-', color: '#D9D9D9', imgSrc: '' }
  if (value < 0) {
    return {
      label: 'general__text__down',
      color: '#FF4D4F',
      imgSrc: 'Icon-Trending-Down.svg'
    }
  } else if (value > 0) {
    return {
      label: 'general__text__up',
      color: '#73D13D',
      imgSrc: 'Icon-Trending-Up.svg'
    }
  } else {
    return {
      label: 'general__text__unchanged',
      color: '#40A9FF',
      imgSrc: 'Icon-Trending.svg'
    }
  }
}

export function getHeader(locale: Ref<string>): string[] {
  return [
    { sv: 'Typ', en: 'Type' },
    { sv: 'Värde', en: 'Value' }
  ].map((x) => x[locale.value])
}

export function getRows(
  data: KpiSummary | KpiValue | undefined,
  kpi: Kpi | undefined,
  year: number | undefined,
  locale: Ref<string>
): [string, any][] {
  if (data === undefined) return []
  const yearTo = year
  const yearFrom = year ? year - 1 : undefined
  switch (kpi?.kpi) {
    case 'BUILDING_SCORE':
      return [
        [
          { sv: 'Attraktivitet', en: 'Attractiveness' }[locale.value] ?? '',
          formatNumber(data.metadata.attractiveness, 1)
        ],
        [
          { sv: 'Skick', en: 'Condition' }[locale.value] ?? '',
          formatNumber(data.metadata.condition, 1)
        ],
        [
          { sv: 'Area', en: 'Area' }[locale.value] ?? '',
          `${formatNumber(data.metadata.area, 0)} m²`
        ]
      ]
    case 'TREND': {
      const dataLabel = {
        ATTRACTIVENESS: { sv: 'Attraktivitet', en: 'Attractiveness' },
        CONDITION: { sv: 'Skick', en: 'Condition' }
      }[kpi.parameters?.type ?? ''] ?? { sv: '', en: '' }
      return [
        [dataLabel[locale.value] + '  ' + yearTo, formatNumber(data.metadata.to, 1)],
        [dataLabel[locale.value] + '  ' + yearFrom, formatNumber(data.metadata.from, 1)]
      ]
    }
    default:
      return []
  }
}

export const colorScale: Record<KpiType, ColorScaleConfig> = {
  BUILDING_SCORE: {
    type: 'STEP',
    cutoffs: [
      { color: '#D9D9D9' },
      { value: 2, color: '#FF4D4F' },
      { value: 3.5, color: '#40A9FF' },
      { value: 4.5, color: '#73D13D' }
    ]
  },
  TREND: {
    type: 'STEP',
    cutoffs: [
      { color: '#FF4D4F' },
      { value: -0.5, color: '#40A9FF' },
      { value: 0.5, color: '#73D13D' }
    ]
  }
}
